import { isAfter, isBefore, max, min, parseISO } from "date-fns";
import { Period } from "./types";

export interface DateInterval {
    start: Date;
    end: Date;
}

export const OPEN_INTERVAL_END = parseISO("2099-12-31");

export type ParsedWindow = {
    start: Date | null;
    end: Date | null;
};

export type ClipResult = DateInterval | "outside_period";

function parsePeriodToWindow(period: Period): ParsedWindow {
    return {
        start: period.start ? parseISO(period.start) : null,
        end: period.end ? parseISO(period.end) : null,
    };
}

export function parseOptionalPeriodToWindow(
    period: Period | null,
): ParsedWindow {
    if (period === null) {
        return { start: null, end: null };
    }
    return parsePeriodToWindow(period);
}

function toInterval(period: Period, openEnd: Date): DateInterval {
    if (!period.start) {
        throw new Error(
            "Cannot build an interval from a period without a start",
        );
    }
    return {
        start: parseISO(period.start),
        end: period.end ? parseISO(period.end) : openEnd,
    };
}

function clipDocToWindow(docPeriod: Period, window: ParsedWindow): ClipResult {
    const intervalEnd = window.end ?? OPEN_INTERVAL_END;
    const interval = toInterval(docPeriod, intervalEnd);

    if (window.end && isAfter(interval.start, window.end)) {
        return "outside_period";
    }
    if (window.start && isBefore(interval.end, window.start)) {
        return "outside_period";
    }

    return {
        start: window.start
            ? max([interval.start, window.start])
            : interval.start,
        end: window.end ? min([interval.end, window.end]) : interval.end,
    };
}

export function clipDocToRequirementPeriod(
    docPeriod: Period,
    requirementPeriod: Period,
): ClipResult {
    return clipDocToWindow(docPeriod, parsePeriodToWindow(requirementPeriod));
}

export function clipDocToOptionalWindow(
    docPeriod: Period,
    requirementPeriod: Period | null,
): ClipResult {
    if (requirementPeriod === null) {
        return toInterval(docPeriod, OPEN_INTERVAL_END);
    }
    return clipDocToWindow(docPeriod, parsePeriodToWindow(requirementPeriod));
}
